'use client'

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'

/**
 * What an error page caught, reported to Sentry — once, with Next's digest.
 *
 * A boundary's catch fires no `window` error event, so the SDK's global
 * handlers never see a crash an error page shows: it is reported here or not
 * at all. Until 2026-10-06 only global-error.tsx and Phishing did, and
 * app/error.tsx and seven module boundaries — whose pages show the digest as a
 * support reference — reported nothing, so that reference led nowhere.
 *
 * The digest goes on as a `digest` tag: what the page shows is what support
 * searches Sentry for (`digest:1748392011`). A crash in the browser has none
 * and is reported without one.
 *
 * Once per error object: React runs effects twice under StrictMode in
 * development, a re-render repeats the same error, and Phishing's boundary
 * used to report beside its panel. A WeakSet remembers what was sent without
 * keeping any error alive.
 *
 * Through the app's existing setup (instrumentation-client.ts): without a DSN
 * the SDK is never initialised and `captureException` does nothing.
 */
const reported = new WeakSet<object>()

export function captureCaughtError(error: Error & { digest?: string }): void {
  if (typeof error === 'object' && error !== null) {
    if (reported.has(error)) return
    reported.add(error)
  }
  const digest = typeof error?.digest === 'string' && error.digest ? error.digest : undefined
  if (digest) Sentry.captureException(error, { tags: { digest } })
  else Sentry.captureException(error)
}

/** `captureCaughtError` on mount and whenever the error changes. */
export function useCaptureCaughtError(error: Error & { digest?: string }): void {
  useEffect(() => {
    captureCaughtError(error)
  }, [error])
}
